import { useState, useRef, useCallback, useEffect } from 'react';
import { FavoritePriceShowAs, IFavoriteOriginWithMonths, ModeValueRow, MonthHalf } from '../types/IFavoriteOrigins';

export const usePriceDragCopy = (
  setDraftPriceShowAs: React.Dispatch<React.SetStateAction<Record<string, FavoritePriceShowAs>>>,
  setDraftPrices: React.Dispatch<React.SetStateAction<Record<string, string>>>,
  parsePriceInput: (raw: string | undefined) => number | null,
  onDragCommit?: (
    prices: Record<string, string>,
    showAs: Record<string, FavoritePriceShowAs>,
  ) => void,
) => {
  const [isPriceDragCopying, setIsPriceDragCopying] = useState(false);
  const [dragHoverPriceKey, setDragHoverPriceKey] = useState<string | null>(null);

  const dragPricePayloadRef = useRef<{
    sourceKey: string;
    value: string;
    showAs: FavoritePriceShowAs;
  } | null>(null);
  const dragTargetsRef = useRef<Map<string, FavoritePriceShowAs>>(new Map());

  const normalizePriceDraftText = (raw: string, showAs: FavoritePriceShowAs): string => {
    if (showAs === "DISCUSS") return "Discuss";
    if (showAs === "NO_OFFER") return "No Offer";
    const parsed = parsePriceInput(raw);
    return parsed === null ? "" : String(parsed);
  };

  const stopPriceDragCopy = useCallback(() => {
    setIsPriceDragCopying(false);
    setDragHoverPriceKey(null);
    dragPricePayloadRef.current = null;
    dragTargetsRef.current.clear();
    if (typeof document !== "undefined") {
      document.body.classList.remove("dom-price-drag-copying");
    }
  }, []);

  const commitPriceDragCopy = useCallback(() => {
    const payload = dragPricePayloadRef.current;
    if (payload && dragTargetsRef.current.size > 0) {
      const prices: Record<string, string> = {};
      const showAs: Record<string, FavoritePriceShowAs> = {};
      dragTargetsRef.current.forEach((targetShowAs, targetKey) => {
        prices[targetKey] = normalizePriceDraftText(payload.value, targetShowAs);
        showAs[targetKey] = targetShowAs;
      });
      setDraftPriceShowAs((prev) => ({ ...prev, ...showAs }));
      setDraftPrices((prev) => ({ ...prev, ...prices }));
      
      onDragCommit?.(prices, showAs);
    }
    stopPriceDragCopy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPriceDragCopy, onDragCommit]);

  useEffect(() => {
    if (!isPriceDragCopying) return undefined;
    const handlePointerUp = () => commitPriceDragCopy();
    window.addEventListener("pointerup", handlePointerUp);
    return () => window.removeEventListener("pointerup", handlePointerUp);
  }, [isPriceDragCopying, commitPriceDragCopy]);

  useEffect(() => () => {
    if (typeof document !== "undefined") {
      document.body.classList.remove("dom-price-drag-copying");
    }
  }, []);

  const startPriceDragCopy = (
    event: React.PointerEvent<HTMLButtonElement>,
    origin: IFavoriteOriginWithMonths,
    modeRow: ModeValueRow,
    month: string,
    half: MonthHalf,
    getPriceInputKey: Function,
    getPriceShowAsValue: Function,
    getPriceInputValue: Function
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.currentTarget && typeof event.currentTarget.releasePointerCapture === "function") {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Ignore if pointer capture was not set
      }
    }

    const sourceKey = getPriceInputKey(origin, modeRow.key, month, half);
    const sourceShowAs = getPriceShowAsValue(origin, modeRow, month, half) ?? "PRICE";
    const sourceValue = getPriceInputValue(origin, modeRow, month, half);

    dragPricePayloadRef.current = { sourceKey, value: sourceValue, showAs: sourceShowAs };
    dragTargetsRef.current.clear();
    setDragHoverPriceKey(sourceKey);
    setIsPriceDragCopying(true);

    if (typeof document !== "undefined") {
      document.body.classList.add("dom-price-drag-copying");
    }
  };

  const applyDraggedPriceToInput = (
    origin: IFavoriteOriginWithMonths,
    modeKey: string,
    month: string,
    half: MonthHalf,
    getPriceInputKey: Function
  ) => {
    const payload = dragPricePayloadRef.current;
    if (!payload) return;

    const targetKey = getPriceInputKey(origin, modeKey, month, half);
    if (targetKey === dragHoverPriceKey) return;

    const targetShowAs: FavoritePriceShowAs =
      origin.competitor && payload.showAs === "DISCUSS" ? "PRICE" : payload.showAs;

    if (targetKey !== payload.sourceKey) {
      dragTargetsRef.current.set(targetKey, targetShowAs);
      const normalizedValue = normalizePriceDraftText(payload.value, targetShowAs);
      setDraftPrices((prev) => ({
        ...prev,
        [targetKey]: normalizedValue,
      }));
      setDraftPriceShowAs((prev) => ({
        ...prev,
        [targetKey]: targetShowAs,
      }));
    }
    setDragHoverPriceKey(targetKey);
  };

  return {
    isPriceDragCopying,
    dragHoverPriceKey,
    startPriceDragCopy,
    applyDraggedPriceToInput,
  };
};